import { ArrowUpTrayIcon, XMarkIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { BUNDLED_PRINT_FONTS } from 'shared-types';
import { memo, useMemo, useRef, useState } from 'react';
import { Button } from '../../components/Button';
import { Input } from '../../components/inputs/Input';
import { TextArea } from '../../components/inputs/TextArea';
import { Modal } from '../../components/Modal';
import { T, useT } from '../../translation/src';
import { PrintFontWeight, useOrganisationPrintFontRemoveMutation, useOrganisationTemplateLibraryUpdateConfigMutation, type TemplateLibraryConfigFieldsFragment } from '../../types/graphqlTypes';
import { BRAND_FIELDS, type BrandTextDetail } from './brandDetails';
import { configInputOf } from './configInput';
import { useTemplateUploads } from './useTemplateUploads';

type BrandKey = BrandTextDetail;

const errorCode = (e: unknown): string => (e instanceof Error ? e.message : '');

interface BrandKitModalProps {
  open: boolean;
  onClose: () => void;
  organisationId: string;
  config: TemplateLibraryConfigFieldsFragment;
}

/**
 * The organisation's print identity, shared by every template: contact
 * details (the organisation fields), the logo (logo images) and its own
 * fonts next to the built-in ones, embedded into exported PDFs.
 */
const BrandKitModal_: React.FC<BrandKitModalProps> = ({ open, onClose, organisationId, config }) => {
  const [brand, setBrand] = useState<Record<BrandKey, string>>(() => ({
    address: config.brand.address ?? '',
    phone: config.brand.phone ?? '',
    email: config.brand.email ?? '',
    website: config.brand.website ?? '',
    vat: config.brand.vat ?? '',
  }));
  const [busy, setBusy] = useState<'save' | 'logo' | 'font' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const logoInput = useRef<HTMLInputElement>(null);
  const fontInput = useRef<HTMLInputElement>(null);
  const { uploadLogo, removeLogo, uploadFont } = useTemplateUploads(organisationId);
  const [updateConfig] = useOrganisationTemplateLibraryUpdateConfigMutation({ refetchQueries: ['organisationTemplateLibrary', 'projectTemplateLibrary'] });
  const [removeFont] = useOrganisationPrintFontRemoveMutation({ refetchQueries: ['organisationTemplateLibrary'] });

  const labels = { regular: useT('regular', { swc: true }), bold: useT('bold', { swc: true }), italic: useT('italic', { swc: true }), boldItalic: useT('bold italic', { swc: true }) };
  const messages = {
    invalidImage: useT('the logo must be a PNG, JPEG or SVG file of at most 20 MB', { swc: true }),
    invalidFont: useT('fonts must be TrueType (.ttf) or OpenType (.otf) files of at most 10 MB', { swc: true }),
    fontInUse: useT('this font is still used by a template', { swc: true }),
    notAFont: useT('{name} is not a font file that can be read', { swc: true }),
    removeStyle: useT('remove this style', { swc: true }),
    failed: useT('saving failed', { swc: true }),
  };

  const run = async (kind: 'save' | 'logo' | 'font', action: () => Promise<void>, failure: (code: string) => string) => {
    setBusy(kind);
    setError(null);
    try {
      await action();
    } catch (e) {
      setError(failure(errorCode(e)));
    } finally {
      setBusy(null);
    }
  };

  // Uploaded fonts by family, regular first.
  const families = useMemo(() => {
    const byFamily = new Map<string, TemplateLibraryConfigFieldsFragment['fonts']>();
    for (const font of config.fonts) byFamily.set(font.family, [...(byFamily.get(font.family) ?? []), font]);
    const rank = (f: TemplateLibraryConfigFieldsFragment['fonts'][number]) => (f.weight === PrintFontWeight.Bold ? 2 : 0) + (f.italic ? 1 : 0);
    return [...byFamily.entries()].map(([family, styles]) => [family, [...styles].sort((a, b) => rank(a) - rank(b))] as const);
  }, [config.fonts]);

  const styleName = (font: TemplateLibraryConfigFieldsFragment['fonts'][number]) => (font.weight === PrintFontWeight.Bold ? (font.italic ? labels.boldItalic : labels.bold) : font.italic ? labels.italic : labels.regular);

  /** Each file's family and style come from the font itself. */
  const uploadFonts = (files: File[]) =>
    run(
      'font',
      async () => {
        const { describeFontFile } = await import('../print/engine');
        for (const file of files) {
          const description = describeFontFile(new Uint8Array(await file.arrayBuffer()));
          if (!description) throw new Error(`NOT_A_FONT:${file.name}`);
          await uploadFont(file, description.family, description.bold ? PrintFontWeight.Bold : PrintFontWeight.Normal, description.italic);
        }
      },
      (code) => (code.startsWith('NOT_A_FONT:') ? messages.notAFont.replace('{name}', code.slice('NOT_A_FONT:'.length)) : code === 'INVALID_FILE' || code === 'TOO_LARGE' ? messages.invalidFont : messages.failed),
    );

  const saveBrand = () =>
    run(
      'save',
      async () => {
        await updateConfig({ variables: { organisationId, config: { ...configInputOf(config), brand } } });
        onClose();
      },
      () => messages.failed,
    );

  return (
    <Modal.Container open={open} onClose={onClose} panelClassName="max-w-2xl w-full">
      <Modal.Header onClose={onClose}>
        <T _str="brand kit" swc />
      </Modal.Header>
      <div className="flex flex-col gap-6 text-left text-sm">
        <section>
          <h3 className="mb-2 font-semibold">
            <T _str="logo" swc />
          </h3>
          <div className="flex items-center gap-4">
            <div className="flex h-20 w-40 items-center justify-center rounded border border-gray-200 bg-white p-2 dark:border-gray-600">
              {config.logo ? <img src={config.logo.url} alt="" className="max-h-full max-w-full object-contain" /> : <span className="text-xs text-gray-400">{<T _str="no logo" swc />}</span>}
            </div>
            <div className="flex flex-col gap-2">
              <Button variant="secondary" size="xs" loading={busy === 'logo'} onClick={() => logoInput.current?.click()}>
                <ArrowUpTrayIcon className="mr-1 w-4" />
                {config.logo ? <T _str="replace" swc /> : <T _str="upload" swc />}
              </Button>
              {config.logo && (
                <Button variant="tertiary" size="xs" onClick={() => void run('logo', removeLogo, () => messages.failed)}>
                  <T _str="remove" swc />
                </Button>
              )}
            </div>
            <input
              ref={logoInput}
              type="file"
              accept="image/png,image/jpeg,image/svg+xml"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (file)
                  void run(
                    'logo',
                    async () => {
                      await uploadLogo(file);
                    },
                    (code) => (code === 'INVALID_FILE' || code === 'TOO_LARGE' ? messages.invalidImage : messages.failed),
                  );
              }}
            />
          </div>
          <p className="mt-2 text-xs text-gray-500">
            <T _str="templates place it with a logo image; vector SVG logos stay sharp at any size" swc />
          </p>
        </section>

        <section>
          <h3 className="mb-2 font-semibold">
            <T _str="contact details" swc />
          </h3>
          <div className="grid grid-cols-[8rem_1fr] items-start gap-2">
            {BRAND_FIELDS.map((field) => (
              <label key={field.key} className="contents">
                <span className="pt-2.5 text-gray-600 dark:text-gray-300">
                  <T _str={field.label} swc />
                </span>
                {field.multiline ? (
                  <TextArea value={brand[field.key]} onChange={(e) => setBrand((b) => ({ ...b, [field.key]: e.target.value }))} />
                ) : (
                  <Input value={brand[field.key]} onChange={(e) => setBrand((b) => ({ ...b, [field.key]: e.target.value }))} />
                )}
              </label>
            ))}
          </div>
        </section>

        <section className="flex flex-col gap-3">
          <h3 className="font-semibold">
            <T _str="fonts" swc />
          </h3>
          <div className="flex flex-col gap-1.5">
            <span className="text-xs text-gray-500 dark:text-gray-400">
              <T _str="built in, always available in every template" swc />
            </span>
            <div className="flex flex-wrap gap-1.5">
              {BUNDLED_PRINT_FONTS.map((family) => (
                <span key={family} className="rounded-md bg-gray-100 px-2 py-1 text-xs dark:bg-white/10">
                  {family}
                  {family === 'osifont' && (
                    <span className="text-gray-500">
                      {' · '}
                      <T _str="technical lettering" />
                    </span>
                  )}
                </span>
              ))}
            </div>
          </div>
          <div className="flex flex-col gap-1.5">
            <span className="text-xs text-gray-500 dark:text-gray-400">
              <T _str="your own fonts" swc />
            </span>
            {families.length > 0 ? (
              <ul className="divide-y divide-gray-200 rounded-lg border border-gray-200 dark:divide-gray-700 dark:border-gray-700">
                {families.map(([family, styles]) => (
                  <li key={family} className="flex flex-wrap items-center gap-2 px-3 py-2">
                    <span className="mr-auto font-medium">{family}</span>
                    {styles.map((font) => (
                      <span key={font.id} title={font.originalName} className="flex items-center gap-1 rounded-md bg-french-violet-600/10 py-0.5 pl-2 pr-1 text-xs text-french-violet-700 dark:bg-neon-green-300/10 dark:text-neon-green-300">
                        <span className={classNames(font.weight === PrintFontWeight.Bold && 'font-bold', font.italic && 'italic')}>{styleName(font)}</span>
                        <button
                          type="button"
                          aria-label={messages.removeStyle}
                          title={messages.removeStyle}
                          className="rounded p-0.5 opacity-60 hover:bg-black/10 hover:opacity-100 dark:hover:bg-white/15"
                          onClick={() =>
                            void run(
                              'font',
                              async () => void (await removeFont({ variables: { organisationId, fontId: font.id } })),
                              (code) => (code === 'FONT_IN_USE' ? messages.fontInUse : messages.failed),
                            )
                          }
                        >
                          <XMarkIcon className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-gray-500">
                <T _str="none yet" swc />
              </p>
            )}
            <div>
              <Button variant="secondary" size="xs" loading={busy === 'font'} onClick={() => fontInput.current?.click()}>
                <ArrowUpTrayIcon className="mr-1 w-4" />
                <T _str="upload fonts" swc />
              </Button>
            </div>
            <input
              ref={fontInput}
              type="file"
              multiple
              accept=".ttf,.otf,font/ttf,font/otf"
              className="hidden"
              onChange={(e) => {
                const files = [...(e.target.files ?? [])];
                e.target.value = '';
                if (files.length > 0) void uploadFonts(files);
              }}
            />
            <p className="text-xs text-gray-500">
              <T _str="pick the TTF or OTF files of each style you need (regular, bold, italic…); their family and style are read from the files. Only use fonts your licence allows to embed in documents." swc />
            </p>
          </div>
        </section>

        {error && <p className="text-sm text-error-red-500">{error}</p>}
      </div>
      <Modal.Footer className="justify-end gap-2">
        <Button variant="secondary" size="xs" onClick={onClose}>
          <T _str="cancel" swc />
        </Button>
        <Button variant="primary" size="xs" loading={busy === 'save'} onClick={() => void saveBrand()}>
          <T _str="save" swc />
        </Button>
      </Modal.Footer>
    </Modal.Container>
  );
};

export const BrandKitModal = memo(BrandKitModal_);
